/* eslint-disable @next/next/no-img-element */
import { ArrowRightIcon } from 'lucide-react';
import { type ReactNode } from 'react';
import { type PhotoPrintStep } from './PhotoPrintFlowTypes';
import { normalizeStepForTabs, stopPhotoPrintButtonEvent } from './PhotoPrintFlowUtils';

export function PhotoPrintShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mx-auto mb-4 flex w-full max-w-7xl flex-col rounded-none bg-white px-4 py-4 sm:px-5 md:rounded-xl md:px-5 md:py-5">
      <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <div className="relative h-8 w-8 shrink-0">
              <img src="/photo-print.png" alt="Fotoğraf Baskısı" className="h-8 w-8 object-contain" />
            </div>
          <h3 className="truncate text-base font-semibold text-hzc_beige_900 sm:text-lg">
            {title}
          </h3>
        </div>
        <span className="shrink-0 rounded-full bg-hzc_green px-3 py-1 text-xs font-semibold text-hzc_purple_500">
          Yeni Özellik
        </span>
      </div>

      <p className="mb-5 max-w-2xl text-xs leading-5 text-hzc_beige_800 sm:text-sm">
        Hediyeni fotoğrafınla özelleştir. Çiçeğinizle birlikte basılı olarak gönderiyoruz.
      </p>

      {children}
    </section>
  );
}

export function PhotoFlowShell({
  title,
  step,
  onBack,
  children,
  footer,
  className = '',
}: {
  title: string;
  step: PhotoPrintStep;
  onBack?: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <section className="fixed inset-0 z-50 flex overflow-y-auto bg-hzc_beige_200 lg:bg-[#f3f2ed]">
      <div className="mx-auto flex min-h-screen w-full flex-col bg-hzc_beige_200 lg:max-w-none lg:bg-[#f3f2ed]">
        <div className="bg-hzc_purple_500 px-4 pb-4 pt-6 text-white lg:px-8 lg:pb-6 lg:pt-8">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={(event) => {
                stopPhotoPrintButtonEvent(event);
                onBack?.();
              }}
              className="text-2xl leading-none"
              aria-label="Geri"
            >
              ←
            </button>
            <h3 className="flex-1 text-center text-base font-semibold sm:text-lg lg:text-2xl">
              {title}
            </h3>
            <span className="w-6" />
          </div>
          <PhotoFlowTabs activeStep={step} />
        </div>

        <div
          className={`mx-auto flex w-full max-w-[572px] flex-1 flex-col gap-4 px-4 py-4 max-[380px]:px-3 sm:px-6 sm:py-5 lg:max-w-7xl lg:gap-8 lg:p-8 ${className}`}
        >
          {children}
        </div>

        {footer && (
          <div className="sticky bottom-0 mt-auto bg-hzc_purple_500 p-4 lg:px-8">
            <div className="mx-auto w-full max-w-[572px] lg:max-w-7xl">{footer}</div>
          </div>
        )}
      </div>
    </section>
  );
}

export function PhotoFlowFooter({
  backLabel,
  nextLabel,
  onBack,
  onNext,
}: {
  backLabel: string;
  nextLabel: string;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <div className="grid grid-cols-[112px_1fr] gap-3 lg:ml-auto lg:max-w-[520px]">
      <button
        type="button"
        onClick={(event) => {
          stopPhotoPrintButtonEvent(event);
          onBack();
        }}
        className="h-12 rounded-xl bg-hzc_purple_300 text-sm font-bold text-white"
      >
        {backLabel}
      </button>
      <button
        type="button"
        onClick={(event) => {
          stopPhotoPrintButtonEvent(event);
          onNext();
        }}
        className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[#D2F25C] text-sm font-bold text-hzc_purple_500"
      >
        {nextLabel}
        <ArrowRightIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

function PhotoFlowTabs({ activeStep }: { activeStep: PhotoPrintStep }) {
  const items = [
    { id: 'source', label: 'Kaynak' },
    { id: 'crop', label: 'Kırp' },
    { id: 'filter', label: 'Filtre' },
    { id: 'preview', label: 'Önizleme' },
  ] as const;

  const activeIndex = items.findIndex((item) => item.id === normalizeStepForTabs(activeStep));

  return (
    <div className="mx-auto mt-5 grid w-full max-w-[572px] grid-cols-4 gap-2 text-center text-[11px] font-semibold sm:gap-4 sm:text-xs lg:max-w-5xl lg:text-sm">
      {items.map((item, index) => {
        const isDoneOrActive = activeIndex >= index;
        return (
          <span
            key={item.id}
            className={`border-b-4 pb-2 ${
              isDoneOrActive ? 'border-[#D2F25C] text-[#D2F25C]' : 'border-white/60 text-white/80'
            }`}
          >
            {item.label}
          </span>
        );
      })}
    </div>
  );
}
